/**
 * Normalizer + client-side filter for the Assign Student dialog's eligible-student
 * dropdown.
 *
 * This mirrors the stable pattern the Class Schedule list filter already uses
 * (see filterOptions.ts + index.tsx): the eligible list is loaded ONCE per
 * (class, grade-override) and searched client-side against a lowercased haystack.
 * The dropdown never feeds its input text back into the server query, so it can
 * never enter a refetch/reconciliation loop when a student is selected.
 *
 * `label` is a stable display string built from name/grade/reference — it is
 * ONLY shown, never re-used as a server search term. `search` is the haystack
 * for client-side filtering. `id` is the option identity used for equality.
 */

export interface EligibleStudentOption {
  id: number;
  name: string;
  grade: string;
  referenceId: string;
  perClassRate: number | null;
  label: string;
  search: string;
}

const pickStudents = (response: any): any[] => {
  const src = response?.data ?? response;
  if (Array.isArray(src?.students)) return src.students;
  if (Array.isArray(src?.data?.students)) return src.data.students;
  if (Array.isArray(response?.students)) return response.students;
  if (Array.isArray(src)) return src;
  return [];
};

const nameOf = (r: any): string =>
  (r?.name ||
    r?.full_name ||
    [r?.first_name, r?.middle_name, r?.last_name].filter(Boolean).join(" ") ||
    "").trim();

/** Map an eligible-students API response to stable, deduplicated dropdown options. */
export const buildEligibleStudentOptions = (response: any): EligibleStudentOption[] => {
  const seen = new Set<number>();
  return pickStudents(response)
    .map((r) => {
      const id = Number(r?.id);
      const name = nameOf(r) || `Student #${id}`;
      const grade = String(r?.grade ?? "").trim();
      const referenceId = String(r?.reference_id ?? r?.student_code ?? "").trim();
      const rawRate = r?.per_class_rate;
      const perClassRate =
        rawRate === null || rawRate === undefined || rawRate === "" ? null : Number(rawRate);
      const label = `${name}${grade ? ` · ${grade}` : ""}${referenceId ? ` (${referenceId})` : ""}`;
      return {
        id,
        name,
        grade,
        referenceId,
        perClassRate: Number.isFinite(perClassRate as number) ? (perClassRate as number) : null,
        label,
        // Include the display label so that when MUI reopens the dropdown with a
        // selection (passing the selected label as inputValue to filterOptions),
        // the chosen option still matches and stays visible in the list.
        search: `${name} ${grade} ${referenceId} ${label}`.toLowerCase(),
      };
    })
    .filter((o) => {
      // Numeric id + de-dupe: a student can never appear twice in the dropdown.
      if (!Number.isFinite(o.id) || seen.has(o.id)) return false;
      seen.add(o.id);
      return true;
    });
};

/** Client-side filter over the loaded options (used by Autocomplete.filterOptions). */
export const filterEligibleStudents = (
  opts: EligibleStudentOption[],
  inputValue: string
): EligibleStudentOption[] => {
  const q = (inputValue ?? "").trim().toLowerCase();
  return q ? opts.filter((o) => o.search.includes(q)) : opts;
};
